import cn from 'classnames';

import { Text } from '@app/components/Text';
import { TagsPickerModal } from '@app/shared/components';
import { divideAmount } from '@app/utils';

import { useLeadCardContext } from '../../context';
import { AddActions } from '../AddActions';
import { AddCommentModal } from '../AddCommentModal';
import { AddFileModal } from '../AddFileModal';
import { CheckboxSection } from '../CheckboxSection';
import { LeadFunnelStages } from '../LeadFunnelStages';
import { OrganizationCommentModal } from '../OrganizationCommentModal';
import { RecordBranchModal } from '../RecordBranchModal';
import { ResponsibleSelect } from '../ResponsibleSelect';
import { UserAdditionalFields } from '../UserAdditionalFields';
import { UserClientNote } from '../UserClientNote';
import { UserContactMethods } from '../UserContactMethods';
import { UserDetailsSkeleton } from '../UserDetailsSkeleton';
import { UserProfileHeader } from '../UserProfileHeader';
import { UserTags } from '../UserTags';

import './UserDetailsPanel.scss';

export const UserDetailsPanel = () => {
  const {
    isUserDetailsLoading,
    selectedLeadUserDetails,
    leadCardData,
    currentTags,
    availableTags,
    selectedTagIds,
    isEditAvailable,
    isTagsModalOpen,
    isTagsLoading,
    isTagsSaving,
    openTagsModal,
    closeTagsModal,
    saveTags,
    selectTags,
    commentText,
    isCommentEditing,
    isCommentModalOpen,
    isCommentSaving,
    closeCommentModal,
    saveComment,
    setCommentText,
    isFileModalOpen,
    files,
    isFilesListLoading,
    isFilesListFetching,
    isUploadingFile,
    isRemovingFile,
    isDownloadingFile,
    closeFileModal,
    attachFile,
    downloadFile,
    removeFile,
    isBranchSelectionModalOpen,
    initialBranchSelectionId,
    isRecordBranchesLoading,
    closeBranchSelectionModal,
    confirmBranchSelection,
    branchSelectionOptions,
    selectedEmployeeId,
    selectResponsibleEmployee,
    sourceItems,
    toggleSource,
    selectAllSources,
    clearSources,
    actionItems,
    toggleAction,
    selectAllActions,
    clearActions,
    isOrganizationCommentModalOpen,
    organizationComment,
    handleCloseOrganizationCommentModal,
    handleSaveOrganizationComment,
    isOrganizationCommentLoading,
  } = useLeadCardContext();

  if (isUserDetailsLoading || !selectedLeadUserDetails) {
    return (
      <section className="UserDetailsPanel">
        <UserDetailsSkeleton />
      </section>
    );
  }

  const {
    profile,
    stats,
    clientNote,
    contactMethods,
    responsibleEmployees,
    isResponsibleChanging,
    isResponsibleChangeDisabled,
  } = selectedLeadUserDetails;

  return (
    <section className="UserDetailsPanel">
      <div className="UserDetailsPanel__header">
        <UserProfileHeader profile={profile} stats={stats} />
      </div>

      <div className="UserDetailsPanel__scroll">
        {organizationComment && (
          <Text pattern="body3">
            Комментарий к организации: {organizationComment}
          </Text>
        )}
        <UserContactMethods items={contactMethods} />

        <UserClientNote note={clientNote} />

        <UserAdditionalFields items={leadCardData?.ClientFields ?? []} />

        {profile.balance !== 0 && (
          <div
            className={cn('UserDetailsPanel__balance', {
              'UserDetailsPanel__balance--negative': profile.balance < 0,
            })}
          >
            <span className="UserDetailsPanel__balance-label">Баланс</span>
            <span className="UserDetailsPanel__balance-value">
              {divideAmount(profile.balance)}
            </span>
          </div>
        )}

        <ResponsibleSelect
          employees={responsibleEmployees}
          selectedEmployeeId={selectedEmployeeId}
          isLoading={isResponsibleChanging}
          isDisabled={isResponsibleChangeDisabled}
          onSelect={selectResponsibleEmployee}
        />

        <LeadFunnelStages />

        {sourceItems.length > 0 && (
          <CheckboxSection
            title="Интеграции"
            items={sourceItems}
            onChange={toggleSource}
            onSelectAll={selectAllSources}
            onClear={clearSources}
          />
        )}

        {actionItems.length > 0 && (
          <CheckboxSection
            title="Действия"
            items={actionItems}
            onChange={toggleAction}
            onSelectAll={selectAllActions}
            onClear={clearActions}
          />
        )}

        <UserTags
          items={currentTags}
          isEditable={isEditAvailable}
          onEdit={openTagsModal}
        />
      </div>

      <div className="UserDetailsPanel__footer">
        <AddActions />
      </div>
      <AddCommentModal
        isOpen={isCommentModalOpen}
        title={
          isCommentEditing
            ? 'Редактировать комментарий'
            : 'Добавить комментарий'
        }
        commentText={commentText}
        isSaving={isCommentSaving}
        onCommentTextChange={setCommentText}
        onSave={saveComment}
        onCancel={closeCommentModal}
      />
      <AddFileModal
        isOpen={isFileModalOpen}
        files={files}
        isFilesLoading={isFilesListLoading || isFilesListFetching}
        isUploadingFile={isUploadingFile}
        isRemovingFile={isRemovingFile}
        isDownloadingFile={isDownloadingFile}
        onAttachFile={attachFile}
        onDownloadFile={downloadFile}
        onRemoveFile={removeFile}
        onClose={closeFileModal}
      />
      <RecordBranchModal
        isOpen={isBranchSelectionModalOpen}
        branches={branchSelectionOptions}
        initialBranchId={initialBranchSelectionId}
        isBranchesLoading={isRecordBranchesLoading}
        onConfirm={confirmBranchSelection}
        onCancel={closeBranchSelectionModal}
      />
      <TagsPickerModal
        open={isTagsModalOpen}
        title="Редактирование тегов"
        okText="Сохранить"
        availableTags={availableTags}
        selectedTagIds={selectedTagIds}
        isTagsLoading={isTagsLoading}
        isSaving={isTagsSaving}
        onSelectedTagIdsChange={selectTags}
        onConfirm={saveTags}
        onCancel={closeTagsModal}
      />
      <OrganizationCommentModal
        open={isOrganizationCommentModalOpen}
        organizationComment={organizationComment}
        handleCloseModal={handleCloseOrganizationCommentModal}
        handleSaveComment={handleSaveOrganizationComment}
        isLoading={isOrganizationCommentLoading}
      />
    </section>
  );
};
